/* assets/plan-builder.js — Bound plan builder v5
   No Recharge bundles: one subscription line per cat, contents in line item properties.
   ?debug=1 → diagnostics + payload   ?reset=1 → clear saved session */

(() => {
  'use strict';

  const root = document.getElementById('bound-wizard');
  if (!root) {
    console.error('[bound] #bound-wizard not found — section did not render');
    return;
  }

  const PARAMS = new URLSearchParams(location.search);
  const DEBUG = PARAMS.has('debug');

  if (PARAMS.has('reset')) {
    try { localStorage.removeItem('bound.wizard'); } catch {}
  }

  const esc = s => String(s).replace(/[<>&"]/g, c =>
    ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c]));

  const fail = (label, err) => {
    root.removeAttribute('data-loading');
    root.innerHTML =
      `<pre style="color:crimson;white-space:pre-wrap;font:13px/1.5 monospace;` +
      `border:2px solid crimson;padding:12px">${esc(label)}\n\n` +
      `${esc(err.message)}\n\n${esc(err.stack || '')}</pre>`;
    console.error('[bound]', label, err);
  };

  const readJson = (id) => {
    const el = document.getElementById(id);
    if (!el) throw new Error(`<script id="${id}"> is missing from the page`);
    const raw = el.textContent.trim();
    if (!raw) throw new Error(`<script id="${id}"> is empty`);
    try {
      return JSON.parse(raw);
    } catch (e) {
      const m = /position (\d+)/.exec(e.message);
      const near = m ? raw.slice(Math.max(0, m[1] - 120), Number(m[1]) + 120) : raw.slice(0, 240);
      throw new Error(`${e.message}\n\n--- near the error ---\n${near}`);
    }
  };

  let CONFIG, CATALOG, ASSETS, KLAVIYO;
  try { CONFIG  = readJson('bound-config');   } catch (e) { return fail('CONFIG PARSE FAILED',  e); }
  try { CATALOG = readJson('bound-variants'); } catch (e) { return fail('CATALOG PARSE FAILED', e); }
  try { ASSETS  = readJson('bound-assets');   } catch (e) { return fail('ASSETS PARSE FAILED',  e); }
  try { KLAVIYO = readJson('bound-klaviyo');  } catch { KLAVIYO = {}; }

  try {
    const FLAVOURS = Array.isArray(CATALOG.flavours) ? CATALOG.flavours : [];
    const PARENTS  = CATALOG.parents || {};
    const TRAY     = CONFIG.traySize || 18;

    const FLAVOUR_KEYS = Object.keys(CONFIG.weights.fillets || {});

    /* ================= RESOLVER ================= */

    const resolveVariant = (flavourKey, unit) =>
      FLAVOURS.find(v => v.flavourKey === flavourKey && v.packSize === unit) || null;

    const anyVariant = (flavourKey) =>
      FLAVOURS.find(v => v.flavourKey === flavourKey) || null;

    const flavourName = (k) => {
      const v = anyVariant(k);
      return v ? v.title.replace(/^Bound\s+/, '') : k;
    };

    const tinImage = (k) => resolveVariant(k, 'single')?.image || anyVariant(k)?.image || '';

    const trayImage = (k) =>
      resolveVariant(k, 'tray18')?.trayImage
      || ASSETS.trays?.[k]
      || ASSETS.tray
      || tinImage(k)
      || '';

    /* ================= HELPERS ================= */

    const isEmail = (v) =>
      typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

    const money = (pence) => '£' + ((pence || 0) / 100).toFixed(2);

    function monthlyVariantFor(portion) {
      const plan = CONFIG.plans[portion];
      if (!plan) return null;
      const vs = PARENTS.monthly?.variants || [];
      return vs.find(v => v.title === plan.monthlyVariantTitle)
          || vs.find(v => String(v.title).replace(/\D/g, '') === String(plan.tinsPerCycle))
          || null;
    }

    const trialPrice = () => CONFIG.trial.price || 0;

    const ph = (label, ratio = '1/1', cls = '') =>
      `<div class="bd-ph ${cls}" style="aspect-ratio:${ratio}"
            role="img" aria-label="${esc(label)} placeholder"><span>${esc(label)}</span></div>`;

    const img = (src, label, ratio = '1/1', cls = '') =>
      src
        ? `<img class="bd-img ${cls}" src="${esc(src)}" alt="${esc(label)}"
               loading="lazy" style="aspect-ratio:${ratio}">`
        : ph(label, ratio, cls);

    const PENDING_ASSETS = [
      'Tray photography — 6 flavours, 18-tin tray',
      'Portion icons — 1 / 2 / 3 tins',
      'Why Bound icons — 4'
    ];

    const OPEN_QUESTIONS = [
      'Portion weights: 70g/150g/220g in the copy vs 75g/225g in the screenshots',
      'Per-tin price is highest on the "Most popular" 72-tin plan — intended?',
      '3PL must read line item properties as the pick list for renewals',
      'Reducing to a 1-tray minimum needs new prices and a renewal-frequency decision'
    ];

    /* ================= TRAYS ================= */

    const trayTotal = (trays) =>
      Object.values(trays || {}).reduce((a, n) => a + n, 0);

    function recommendTrays(portion) {
      const plan = CONFIG.plans[portion];
      if (!plan) return {};
      const w = CONFIG.weights.fillets || {};
      const ordered = [...FLAVOUR_KEYS].sort((a, b) =>
        (w[b] || 0) - (w[a] || 0) || FLAVOUR_KEYS.indexOf(a) - FLAVOUR_KEYS.indexOf(b));

      const out = {};
      let left = plan.trays;
      for (const k of ordered) {
        if (left <= 0) break;
        out[k] = 1; left--;
      }
      let i = 0;
      while (left > 0) { out[ordered[i % ordered.length]]++; left--; i++; }
      return out;
    }

    function trialUnits() {
      const per = Math.floor(CONFIG.trial.tins / FLAVOUR_KEYS.length);
      const out = FLAVOUR_KEYS.map(k => ({ flavourKey: k, qty: per }));
      let left = CONFIG.trial.tins - per * FLAVOUR_KEYS.length;
      let i = 0;
      while (left > 0) { out[i % out.length].qty++; left--; i++; }
      return out.filter(u => u.qty > 0);
    }

    function deriveBoxes(cat) {
      const plan = CONFIG.plans[cat.portion];
      if (!plan) throw new Error(`Unknown portion "${cat.portion}"`);
      const trays = cat.trays && trayTotal(cat.trays) === plan.trays
        ? cat.trays
        : recommendTrays(cat.portion);

      return {
        trial: {
          units: trialUnits().map(u => ({ ...u, unit: 'single' })),
          tins:  CONFIG.trial.tins,
          price: trialPrice()
        },
        renewal: {
          trays,
          units: Object.entries(trays)
            .filter(([, n]) => n > 0)
            .sort(([a], [b]) => FLAVOUR_KEYS.indexOf(a) - FLAVOUR_KEYS.indexOf(b))
            .map(([flavourKey, n]) => ({
              flavourKey, trays: n, tins: n * TRAY, unit: 'tray18'
            })),
          trayCount: plan.trays,
          tins: plan.tinsPerCycle,
          cycleDays: plan.cycleDays,
          firstChargeInDays: CONFIG.firstChargeInDays
        }
      };
    }

    /* ================= VALIDATOR ================= */

    function validate() {
      const errors = [], warnings = [];
      const portions = Object.keys(CONFIG.plans || {});

      if (!FLAVOURS.length)
        errors.push('Variant map is empty — check the flavour collection and product status.');

      if (!PARENTS.monthly || PARENTS.monthly.found !== true)
        errors.push('Monthly Plan parent product not found — check the product handle in Liquid');
      else if (!(PARENTS.monthly.variants || []).length)
        errors.push('Monthly Plan parent has no variants');

      const planIds = (PARENTS.monthly?.variants || []).map(v => v.sellingPlanId);
      if (planIds.some(id => !id))
        errors.push('A Monthly Plan variant has no selling plan — check per-variant assignment in Recharge');
      if (new Set(planIds.filter(Boolean)).size !== planIds.filter(Boolean).length)
        errors.push('Two Monthly Plan variants share a selling plan — the first charge would be wrong for one of them');

      FLAVOUR_KEYS.forEach(k => {
        if (!resolveVariant(k, 'single'))
          errors.push(`No single-tin variant for "${k}" (needs bound_pack_size = single)`);
        if (!resolveVariant(k, 'tray18'))
          errors.push(`No tray variant for "${k}" (needs bound_pack_size = tray18)`);
      });

      for (const portion of portions) {
        const p = CONFIG.plans[portion];

        if (p.trays * TRAY !== p.tinsPerCycle)
          errors.push(`${portion}: ${p.trays} trays × ${TRAY} = ${p.trays * TRAY}, but tinsPerCycle is ${p.tinsPerCycle}`);

        if (p.tinsPerCycle / p.tinsPerDay !== p.cycleDays)
          warnings.push(`${portion}: ${p.tinsPerCycle} tins at ${p.tinsPerDay}/day lasts ` +
            `${p.tinsPerCycle / p.tinsPerDay} days but cycleDays is ${p.cycleDays}`);

        const mv = monthlyVariantFor(portion);
        if (!mv) {
          errors.push(`No Monthly Plan variant "${p.monthlyVariantTitle}" for "${portion}"`);
        } else {
          if (!mv.price) warnings.push(`Monthly Plan variant "${mv.title}" has price 0`);
          if (!mv.sellingPlanId) errors.push(`Monthly Plan variant "${mv.title}" has no selling plan`);
          if (!mv.sku) warnings.push(`Monthly Plan variant "${mv.title}" has no SKU`);
        }

        if (trayTotal(recommendTrays(portion)) !== p.trays)
          errors.push(`${portion}: recommendation does not total ${p.trays} trays`);
      }

      const tTins = trialUnits().reduce((a, u) => a + u.qty, 0);
      if (tTins !== CONFIG.trial.tins)
        errors.push(`Trial box is ${tTins} tins, expected ${CONFIG.trial.tins}`);

      FLAVOURS.forEach(v => {
        if (!v.flavourKey) {
          errors.push(`Variant ${v.variantId} (${v.title || '—'}) has no flavour key metafield`);
          return;
        }
        if (!['single', 'tray18'].includes(v.packSize))
          errors.push(`${v.title} / ${v.variantTitle} has pack size "${v.packSize}" — expected single or tray18`);
        if (!FLAVOUR_KEYS.includes(v.flavourKey))
          warnings.push(`${v.title} → "${v.flavourKey}" is not in the weight table`);
        if (!v.sku)
          warnings.push(`${v.title} / ${v.variantTitle} has no SKU — the 3PL picks on SKU`);
      });

      const seen = new Map();
      FLAVOURS.forEach(v => {
        if (!v.flavourKey || !v.packSize) return;
        const key = `${v.flavourKey}/${v.packSize}`;
        if (seen.has(key)) errors.push(`Duplicate variant for ${key}`);
        else seen.set(key, v.variantId);
      });

      return { errors, warnings };
    }

    /* ================= REPORT (debug only) ================= */

    const { errors, warnings } = validate();
    if (errors.length)   console.error('[bound] catalogue errors:', errors);
    if (warnings.length) console.warn('[bound] catalogue warnings:', warnings);

    const reportEl = document.getElementById('bound-report');
    if (DEBUG && reportEl) {
      const rows = Object.keys(CONFIG.plans).map(portion => {
        const p = CONFIG.plans[portion];
        const mv = monthlyVariantFor(portion);
        const rec = recommendTrays(portion);
        return `<tr>
          <td>${esc(portion)}</td>
          <td>${p.tinsPerDay}/day</td>
          <td>${p.trays} trays = ${p.tinsPerCycle} tins / ${p.cycleDays}d</td>
          <td>${mv ? esc(mv.title) + ' · ' + money(mv.price) : '<span style="color:crimson">none</span>'}</td>
          <td>${mv?.sellingPlanId || '<span style="color:crimson">no plan</span>'}</td>
          <td>${esc(Object.entries(rec).map(([k, n]) => `${n}× ${k}`).join(', '))}</td>
        </tr>`;
      }).join('');

      const skuRows = FLAVOUR_KEYS.map(k => {
        const s = resolveVariant(k, 'single');
        const t = resolveVariant(k, 'tray18');
        return `<tr><td>${esc(k)}</td>
          <td>${s ? esc(s.sku || '(none)') : '<span style="color:crimson">missing</span>'}</td>
          <td>${t ? esc(t.sku || '(none)') : '<span style="color:crimson">missing</span>'}</td></tr>`;
      }).join('');

      const list = (items, colour) =>
        items.length
          ? `<ul style="color:${colour};margin:.5rem 0">${items.map(i => `<li>${esc(i)}</li>`).join('')}</ul>`
          : '';

      reportEl.hidden = false;
      reportEl.innerHTML = `
        <div style="font:14px/1.5 system-ui;max-width:1100px;margin:0 auto 2rem;
                    border-bottom:2px dashed #ccc;padding-bottom:1rem">
          <h2 style="margin:0 0 .25rem">Catalogue diagnostics</h2>
          <p style="margin:0 0 1rem;color:#555">
            config ${esc(CONFIG.version)} · ${FLAVOURS.length} variants · tray ${TRAY} ·
            <strong style="color:${errors.length ? 'crimson' : 'green'}">${errors.length} errors</strong> ·
            <span style="color:#b58900">${warnings.length} warnings</span>
          </p>
          ${list(errors, 'crimson')}
          <details><summary style="cursor:pointer;color:#b58900">${warnings.length} warnings</summary>
            ${list(warnings, '#b58900')}</details>
          <table border="1" cellpadding="6" style="border-collapse:collapse;font-size:13px;width:100%">
            <thead style="background:#f4f4f4"><tr>
              <th>Plan</th><th>Portion</th><th>Cycle</th><th>Variant</th><th>Selling plan</th><th>Recommended</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
          <table border="1" cellpadding="6" style="border-collapse:collapse;font-size:13px;margin-top:.5rem">
            <thead style="background:#f4f4f4"><tr><th>Flavour</th><th>Single SKU</th><th>Tray SKU</th></tr></thead>
            <tbody>${skuRows}</tbody>
          </table>
          <p style="font-size:13px;color:#555">
            Trial: ${esc(trialUnits().map(u => `${u.qty}× ${u.flavourKey}`).join(', '))}
            · ${money(CONFIG.copy.trialCompare)} → ${money(trialPrice())}
          </p>
          <details style="margin-top:.5rem"><summary style="cursor:pointer">Parents</summary>
            <pre style="font-size:12px;background:#f7f7f7;padding:8px;overflow:auto">${esc(JSON.stringify(PARENTS, null, 2))}</pre></details>
        </div>`;
    }

    /* ================= STATE ================= */

    const STORE_KEY = 'bound.wizard';
    const SCHEMA = 5;

    const uid = () => 's_' + Math.random().toString(36).slice(2, 10);

    const freshCat = (n) => ({
      id: 'cat_' + n + '_' + Math.random().toString(36).slice(2, 6),
      name: '',
      age: { value: 1, unit: 'years' },
      portion: 'just_right',
      support: [],
      trays: null,
      traysEdited: false
    });

    const freshState = () => ({
      schemaVersion: SCHEMA,
      configVersion: CONFIG.version,
      sessionId: uid(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      currentStepId: 'cats',
      visitedSteps: ['cats'],
      planView: 'trial',
      editingTrays: null,
      email: null,
      emailCapturedAt: null,
      cats: [freshCat(1)],
      meta: { entryStep: 'cats', referrer: document.referrer || null }
    });

    function restore() {
      try {
        const raw = localStorage.getItem(STORE_KEY);
        if (!raw) return freshState();
        const s = JSON.parse(raw);
        if (s.schemaVersion !== SCHEMA) return freshState();
        if (s.configVersion !== CONFIG.version) return freshState();
        if (Date.now() - s.updatedAt > 14 * 864e5) return freshState();
        if (!Array.isArray(s.cats) || !s.cats.length) return freshState();
        if (!s.planView) s.planView = 'trial';
        return s;
      } catch { return freshState(); }
    }

    const state = restore();

    function persist() {
      state.updatedAt = Date.now();
      try { localStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch {}
    }

    /* ================= STEPS ================= */

    const plural = (s, one, many) => s.cats.length > 1 ? many : one;
    const firstName = (s) => (s.cats[0]?.name || '').trim() || 'your cat';

    const STEPS = [
      {
        id: 'cats',
        enabled: true,
        title: () => 'Tell us their name and age?',
        subtitle: () => 'Every cat is unique, and it helps us tailor their perfect meals.',
        validate: (s) => s.cats.length > 0 &&
          s.cats.every(c => c.name.trim().length > 0 && c.age.value > 0),
        render: renderCats
      },
      {
        id: 'portion',
        enabled: true,
        title: (s) => plural(s,
          `Choose what's right for ${firstName(s)}`,
          "Choose what's right for your cats"),
        subtitle: () => 'You can change this at any time.',
        validate: (s) => s.cats.every(c => !!c.portion),
        render: renderPortion
      },
      { id: 'dislikes', enabled: false },
      { id: 'support',  enabled: false },
      {
        id: 'email',
        enabled: true,
        title: () => "Discover what's on the menu.",
        subtitle: () => "We'll send you an email with our recommended plan.",
        validate: (s) => isEmail(s.email),
        render: renderEmail
      },
      {
        id: 'plan',
        enabled: true,
        terminal: true,
        title: (s) => plural(s,
          `Here is ${firstName(s)}'s personalised meal plan`,
          'Here are your personalised meal plans'),
        validate: () => true,
        render: renderPlan
      }
    ];

    const active  = () => STEPS.filter(s => s.enabled);
    const stepAt  = (id) => active().find(s => s.id === id);
    const indexOf = (id) => active().findIndex(s => s.id === id);

    function goTo(id, { push = true } = {}) {
      if (!stepAt(id)) return false;
      state.currentStepId = id;
      if (!state.visitedSteps.includes(id)) state.visitedSteps.push(id);
      persist();
      if (push) {
        const u = new URL(location.href);
        u.searchParams.set('step', id);
        u.searchParams.delete('reset');
        history.replaceState({ step: id }, '', u);
      }
      render();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return true;
    }

    const canAdvance = () => {
      const s = stepAt(state.currentStepId);
      return s ? s.validate(state) : false;
    };

    function next() {
      if (!canAdvance()) return;
      if (state.currentStepId === 'email') pushToKlaviyo();
      const nxt = active()[indexOf(state.currentStepId) + 1];
      if (nxt) goTo(nxt.id);
    }

    function back() {
      const prev = active()[indexOf(state.currentStepId) - 1];
      if (prev) goTo(prev.id);
    }

    /* ================= COMPONENTS ================= */

    const avatar = (cat) =>
      `<span class="bd-avatar">${esc((cat.name.trim()[0] || '?').toUpperCase())}</span>`;

    function renderCats() {
      const rows = state.cats.map((cat, i) => `
        <div class="bd-catrow" data-cat="${cat.id}">
          <span class="bd-avatar ${ASSETS.cat_icon ? 'bd-avatar--icon' : ''}">
            ${ASSETS.cat_icon
              ? `<img src="${esc(ASSETS.cat_icon)}" alt="" aria-hidden="true">`
              : esc((cat.name.trim()[0] || '?').toUpperCase())}
          </span>

          <div class="bd-field bd-field--name">
            <label class="bd-label" for="name-${cat.id}">Cat's name</label>
            <input class="bd-input" id="name-${cat.id}" type="text" data-field="name"
                   placeholder="e.g. Luna" value="${esc(cat.name)}"
                   autocomplete="off" aria-label="Cat ${i + 1} name">
          </div>

          <div class="bd-field bd-field--age">
            <span class="bd-label">Age</span>
            <div class="bd-agerow">
              <div class="bd-stepper">
                <button type="button" data-act="age-" aria-label="Decrease age">−</button>
                <input class="bd-age" type="number" inputmode="numeric" min="0" max="30"
                       data-field="age" value="${cat.age.value}" aria-label="Age">
                <button type="button" data-act="age+" aria-label="Increase age">+</button>
              </div>
              <select class="bd-unit" data-field="unit" aria-label="Age unit">
                <option value="years"  ${cat.age.unit === 'years'  ? 'selected' : ''}>year(s) old</option>
                <option value="months" ${cat.age.unit === 'months' ? 'selected' : ''}>month(s) old</option>
              </select>
            </div>
          </div>
        </div>`).join('');

      return `
        <div class="bd-card">
          ${rows}
          <div class="bd-catactions">
            <button type="button" class="bd-ghost" data-act="add-cat">+ Add Another Cat</button>
            ${state.cats.length > 1
              ? `<button type="button" class="bd-link" data-act="remove-cat">− Remove Last Cat</button>`
              : ''}
          </div>
        </div>`;
    }

    function renderPortion() {
      return state.cats.map(cat => `
        <div class="bd-card" data-cat="${cat.id}">
          <div class="bd-cathead">${avatar(cat)}<strong>${esc(cat.name || 'Your cat')}</strong></div>
          <div class="bd-options">
            ${Object.entries(CONFIG.plans).map(([key, p]) => `
              <button type="button"
                      class="bd-option ${cat.portion === key ? 'is-selected' : ''}"
                      data-act="portion" data-value="${esc(key)}"
                      aria-pressed="${cat.portion === key}">
                ${p.badge ? `<span class="bd-badge">${esc(p.badge)}</span>` : ''}
                <span class="bd-opt-amount">
                  <span class="bd-opt-tins">${p.tinsPerDay} tin${p.tinsPerDay > 1 ? 's' : ''} a day</span>
                  ${p.grams ? `<span class="bd-opt-grams">(${esc(p.grams)})</span>` : ''}
                </span>
                ${img(ASSETS['portion_' + p.tinsPerDay], `${p.tinsPerDay} tin`, '1/1', 'bd-ph--opt')}
                <span class="bd-opt-title">${esc(p.label || key)}</span>
                <span class="bd-opt-blurb">${esc(p.blurb || '')}</span>
              </button>`).join('')}
          </div>
        </div>`).join('');
    }

    function renderEmail() {
      const bad = state.email && !isEmail(state.email);
      return `
        <div class="bd-card bd-card--narrow">
          <div class="bd-field">
            <label class="bd-label" for="bd-email">Email address</label>
            <input class="bd-input" id="bd-email" type="email" data-field="email"
                   placeholder="you@example.com" value="${esc(state.email || '')}"
                   autocomplete="email" inputmode="email">
          </div>
          <p class="bd-note" ${bad ? '' : 'hidden'}>Please enter a valid email address.</p>
        </div>`;
    }

    function renderTrayEditor(cat) {
      const plan = CONFIG.plans[cat.portion];
      const trays = cat.trays || recommendTrays(cat.portion);
      const used = trayTotal(trays);
      const balanced = used === plan.trays;

      return `
        <div class="bd-editor">
          <p class="bd-sub">
            Pick your trays — each tray is ${TRAY} tins.
            <strong class="${balanced ? '' : 'bd-over'}">${used} of ${plan.trays} chosen</strong>
          </p>
          <div class="bd-trayrows">
            ${FLAVOUR_KEYS.map(k => {
              const n = trays[k] || 0;
              const atMax = used >= plan.trays;
              return `
              <div class="bd-trayrow">
                ${img(trayImage(k), `${flavourName(k)} tray`, '1/1', 'bd-ph--tin')}
                <span class="bd-trayname">${esc(flavourName(k))}</span>
                <div class="bd-stepper">
                  <button type="button" data-act="tray-" data-value="${esc(k)}"
                          ${n === 0 ? 'disabled' : ''} aria-label="Fewer ${esc(flavourName(k))}">−</button>
                  <span class="bd-traycount">${n}</span>
                  <button type="button" data-act="tray+" data-value="${esc(k)}"
                          ${atMax ? 'disabled' : ''} aria-label="More ${esc(flavourName(k))}">+</button>
                </div>
                <span class="bd-traytins">${n ? n * TRAY + ' tins' : ''}</span>
              </div>`;
            }).join('')}
          </div>
          <div class="bd-editoractions">
            <button type="button" class="bd-link" data-act="tray-reset">Reset to our recommendation</button>
            <button type="button" class="bd-ghost" data-act="tray-done" ${balanced ? '' : 'disabled'}>Done</button>
          </div>
        </div>`;
    }

    function renderPlan() {
      const view = state.planView || 'trial';

      const cards = state.cats.map(cat => {
        const boxes = deriveBoxes(cat);
        const mv = monthlyVariantFor(cat.portion);
        const monthly = mv ? mv.price : null;
        const name = esc(cat.name || 'Your cat');
        const editing = state.editingTrays === cat.id;

        const lines = view === 'trial'
          ? boxes.trial.units.map(u => `
              <li>
                ${img(tinImage(u.flavourKey), flavourName(u.flavourKey), '1/1', 'bd-ph--tin')}
                <span class="bd-tinmeta"><span class="bd-qty">${u.qty}×</span>
                  ${esc(flavourName(u.flavourKey))}</span>
              </li>`).join('')
          : boxes.renewal.units.map(u => `
              <li>
                ${img(trayImage(u.flavourKey), `${flavourName(u.flavourKey)} tray`, '1/1', 'bd-ph--tin')}
                <span class="bd-tinmeta"><span class="bd-qty">${u.trays}×</span>
                  ${esc(flavourName(u.flavourKey))}
                  <small>tray — ${u.tins} tins</small></span>
              </li>`).join('');

        return `
        <div class="bd-card" data-cat="${cat.id}">
          <div class="bd-tabs">
            <button type="button" class="bd-tab ${view === 'trial' ? 'is-on' : ''}"
                    data-act="plan-view" data-value="trial">
              Today's Trial<small><s>${money(CONFIG.copy.trialCompare)}</s> ${money(trialPrice())}</small>
            </button>
            <button type="button" class="bd-tab ${view === 'monthly' ? 'is-on' : ''}"
                    data-act="plan-view" data-value="monthly">
              Your Plan<small>${monthly !== null ? money(monthly) : '—'}</small>
            </button>
          </div>

          <div class="bd-cathead">${avatar(cat)}
            <strong>${name}'s ${view === 'trial' ? 'Trial' : 'Plan'}</strong>
            <span class="bd-total">${view === 'trial' ? boxes.trial.tins : boxes.renewal.tins} tins</span>
          </div>

          ${view === 'monthly' ? `<p class="bd-sub">If ${name} loves it, you'll receive
            ${boxes.renewal.trayCount} trays — ${boxes.renewal.tins} tins — every
            ${boxes.renewal.cycleDays} days.</p>` : ''}

          ${editing ? renderTrayEditor(cat) : `
            <ul class="bd-tins">${lines}</ul>
            ${view === 'monthly' ? `
              <div class="bd-editrow">
                <button type="button" class="bd-ghost bd-edit" data-act="tray-edit">Edit recipes</button>
                ${cat.traysEdited ? '<span class="bd-customised">Customised</span>' : ''}
              </div>` : ''}
          `}
        </div>`;
      }).join('');

      return cards + renderWhyBound() + renderPriceBox();
    }

    function renderWhyBound() {
      return `
        <div class="bd-card bd-why">
          <h3>Why Bound?</h3>
          <div class="bd-whygrid">
            ${(CONFIG.copy.whyBound || []).map(([t, d], i) => `
              <div class="bd-whyitem">
                <div class="bd-whyhead">
                  ${img(ASSETS['why_' + (i + 1)], t, '1/1', 'bd-whyicon')}
                  <strong>${esc(t)}</strong>
                </div>
                <span class="bd-whydesc">${esc(d)}</span>
              </div>`).join('')}
          </div>
        </div>`;
    }

    function renderPriceBox() {
      const planTotal = state.cats.reduce((sum, c) => {
        const mv = monthlyVariantFor(c.portion);
        return sum + (mv ? mv.price : 0);
      }, 0);
      const trialTotal = trialPrice() * state.cats.length;
      const days = CONFIG.plans[state.cats[0]?.portion]?.cycleDays || 36;

      return `
        <div class="bd-card bd-pricebox">
          <div class="bd-priceline"><span>Trial Box ×${state.cats.length}</span>
            <span><s>${money(CONFIG.copy.trialCompare * state.cats.length)}</s> ${money(trialTotal)}</span></div>
          <div class="bd-priceline"><span>Shipping</span>
            <span><s>${money(CONFIG.copy.shippingWas)}</s> ${money(0)}</span></div>
          <div class="bd-priceline bd-priceline--total"><span>Total</span>
            <span>${money(trialTotal)}</span></div>
          <button type="button" class="bd-checkout" data-act="checkout">Secure Checkout</button>
          <p class="bd-note">In ${CONFIG.firstChargeInDays} days, if your
            ${plural(state, 'cat loves', 'cats love')} it, you will pay
            ${money(planTotal)} every ${days} days. Cancel, pause or edit anytime. No commitments!</p>
        </div>`;
    }

    /* ================= SHELL ================= */

    function dots() {
      const steps = active().filter(s => !s.terminal);
      const cur = indexOf(state.currentStepId);
      return `<div class="bd-dots">${steps.map((s, i) =>
        `<span class="bd-dot ${i === cur ? 'is-on' : ''} ${i < cur ? 'is-done' : ''}"></span>`
      ).join('')}</div>`;
    }

    const brandHeader = () =>
      ASSETS.logo
        ? `<a class="bd-logolink" href="/" aria-label="Bound home">
             <img class="bd-logo" src="${esc(ASSETS.logo)}" alt="Bound"></a>`
        : '';

    const brandFooter = () =>
      ASSETS.cat ? `<img class="bd-cat" src="${esc(ASSETS.cat)}" alt="" aria-hidden="true">` : '';

    function render() {
      const step = stepAt(state.currentStepId);
      if (!step) { state.currentStepId = active()[0].id; return render(); }

      root.removeAttribute('data-loading');
      root.innerHTML = `
        ${brandHeader()}
        ${dots()}
        <h1 class="bd-title">${esc(step.title(state))}</h1>
        ${step.subtitle ? `<p class="bd-lede">${esc(step.subtitle(state))}</p>` : ''}
        <div class="bd-body">${step.render(state)}</div>
        <div class="bd-nav">
          ${indexOf(step.id) > 0
            ? `<button type="button" class="bd-back" data-act="back">‹ Back</button>` : ''}
          ${step.terminal ? '' : `<button type="button" class="bd-next" data-act="next"
                  ${canAdvance() ? '' : 'disabled'}>Next ›</button>`}
        </div>
        ${brandFooter()}
        ${DEBUG ? `<details class="bd-debug"><summary>state</summary>
          <pre>${esc(JSON.stringify(state, null, 2))}</pre></details>` : ''}`;
    }

    function refreshNav() {
      const btn = root.querySelector('.bd-next');
      if (btn) btn.disabled = !canAdvance();
    }

    const catOf = (el) => {
      const wrap = el.closest('[data-cat]');
      return wrap ? state.cats.find(c => c.id === wrap.dataset.cat) : null;
    };

    /* ================= KLAVIYO ================= */

    let klaviyoSent = null;

    async function pushToKlaviyo() {
      const email = (state.email || '').trim();
      if (!KLAVIYO.publicKey || !isEmail(email)) return;
      if (klaviyoSent === email) return;
      klaviyoSent = email;

      const cat = state.cats[0];
      const plan = cat?.portion ? CONFIG.plans[cat.portion] : null;
      const mv = cat?.portion ? monthlyVariantFor(cat.portion) : null;

      try {
        const res = await fetch(
          `https://a.klaviyo.com/client/profiles/?company_id=${encodeURIComponent(KLAVIYO.publicKey)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'profile',
                attributes: {
                  email,
                  properties: {
                    bound_session: state.sessionId,
                    bound_cat_count: state.cats.length,
                    bound_cat_names: state.cats.map(c => c.name).filter(Boolean).join(', '),
                    bound_portion: cat?.portion || '',
                    bound_plan_tins: plan?.tinsPerCycle || '',
                    bound_plan_price: mv ? mv.price / 100 : '',
                    bound_cycle_days: plan?.cycleDays || '',
                    bound_resume_url: `${location.origin}${location.pathname}?step=plan`,
                    bound_config_version: CONFIG.version
                  }
                }
              }
            })
          }
        );
        if (!res.ok) throw new Error(`Klaviyo ${res.status}`);
      } catch (err) {
        console.warn('[bound] klaviyo push failed', err);
        klaviyoSent = null;
      }
    }

    /* ================= CHECKOUT ================= */

    let checkoutBusy = false;

    const encodeTrays = (units) => units.map(u => `${u.flavourKey}:${u.trays}`).join(',');

    function cartItemFor(cat) {
      const boxes = deriveBoxes(cat);
      const plan = CONFIG.plans[cat.portion];
      const mv = monthlyVariantFor(cat.portion);

      const trialList = boxes.trial.units
        .map(u => `${u.qty}× ${flavourName(u.flavourKey)}`).join(', ');
      const trayList = boxes.renewal.units
        .map(u => `${u.trays}× ${flavourName(u.flavourKey)}`).join(', ');
      const traySkus = boxes.renewal.units
        .map(u => `${resolveVariant(u.flavourKey, 'tray18')?.sku || '?'}×${u.trays}`).join(', ');

      return {
        id: mv.variantId,
        quantity: 1,
        selling_plan: mv.sellingPlanId,
        properties: {
          // visible on the order and packing slip — this is the 3PL's pick list
          'Cat': cat.name,
          'First box': `${CONFIG.trial.tins} tins — ${trialList}`,
          'Then every 36 days': `${boxes.renewal.trayCount} trays — ${trayList}`,
          'Tray SKUs': traySkus,
          // machine-readable
          _bound_trial: 'true',
          _bound_cat_id: cat.id,
          _bound_portion: cat.portion,
          _bound_trays: encodeTrays(boxes.renewal.units),
          _bound_tray_size: String(TRAY),
          _bound_cycle_days: String(plan.cycleDays),
          _bound_first_charge_days: String(CONFIG.firstChargeInDays),
          _bound_session: state.sessionId,
          _bound_plan_v: CONFIG.version
        }
      };
    }

    function setCheckoutBusy(on, label) {
      checkoutBusy = on;
      const btn = root.querySelector('.bd-checkout');
      if (!btn) return;
      btn.disabled = on;
      btn.textContent = label || 'Secure Checkout';
    }

    function showCheckoutError(msg) {
      setCheckoutBusy(false);
      const box = root.querySelector('.bd-pricebox');
      if (!box) { alert(msg); return; }
      let el = box.querySelector('.bd-checkouterr');
      if (!el) {
        el = document.createElement('p');
        el.className = 'bd-checkouterr';
        box.appendChild(el);
      }
      el.textContent = `${msg} Please try again, or contact us if it keeps happening.`;
    }

    async function goToCheckout() {
      if (checkoutBusy) return;

      for (const cat of state.cats) {
        const mv = monthlyVariantFor(cat.portion);
        if (!mv?.variantId || !mv?.sellingPlanId)
          return showCheckoutError('This plan is not configured for checkout.');
      }

      setCheckoutBusy(true, 'Preparing your box…');

      try {
        const items = state.cats.map(cat => cartItemFor(cat));

        await fetch('/cart/clear.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }
        });

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.description || body.message || `The cart rejected the item (${res.status}).`);
        }

        console.info('[bound] cart ok', await res.json());
        window.location.href = '/checkout';

      } catch (err) {
        console.error('[bound] checkout failed', err);
        showCheckoutError(err.message);
      }
    }

    /* ================= EVENTS ================= */

    root.addEventListener('input', (e) => {
      if (e.target.dataset.field === 'email') {
        state.email = e.target.value.trim();
        if (isEmail(state.email) && !state.emailCapturedAt) state.emailCapturedAt = Date.now();
        persist(); refreshNav();
        return;
      }

      const cat = catOf(e.target);
      if (!cat) return;
      const f = e.target.dataset.field;
      if (f === 'name') {
        cat.name = e.target.value;
        const av = e.target.closest('.bd-catrow')?.querySelector('.bd-avatar');
        if (av && !av.classList.contains('bd-avatar--icon')) {
          av.textContent = (cat.name.trim()[0] || '?').toUpperCase();
        }
      } else if (f === 'age') {
        cat.age.value = Math.max(0, Math.min(30, parseInt(e.target.value, 10) || 0));
      }
      persist();
      refreshNav();
    });

    root.addEventListener('change', (e) => {
      const cat = catOf(e.target);
      if (cat && e.target.dataset.field === 'unit') {
        cat.age.unit = e.target.value;
        persist();
      }
    });

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-act]');
      if (!btn) return;
      const act = btn.dataset.act;
      const cat = catOf(btn);

      switch (act) {
        case 'next': next(); break;
        case 'back': back(); break;

        case 'add-cat':
          state.cats.push(freshCat(state.cats.length + 1));
          persist(); render();
          root.querySelector('.bd-catrow:last-of-type .bd-input')?.focus();
          break;

        case 'remove-cat':
          if (state.cats.length > 1) { state.cats.pop(); persist(); render(); }
          break;

        case 'age+':
        case 'age-': {
          if (!cat) break;
          cat.age.value = Math.max(0, Math.min(30, cat.age.value + (act === 'age+' ? 1 : -1)));
          const input = btn.parentElement.querySelector('.bd-age');
          if (input) input.value = cat.age.value;
          persist(); refreshNav();
          break;
        }

        case 'portion':
          if (!cat) break;
          if (cat.portion !== btn.dataset.value) {
            cat.portion = btn.dataset.value;
            cat.trays = recommendTrays(cat.portion);
            cat.traysEdited = false;
          }
          persist(); render();
          break;

        case 'plan-view':
          state.planView = btn.dataset.value;
          state.editingTrays = null;
          persist(); render();
          break;

        case 'tray-edit':
          if (!cat) break;
          if (!cat.trays) cat.trays = recommendTrays(cat.portion);
          state.editingTrays = cat.id;
          persist(); render();
          break;

        case 'tray-done':
          state.editingTrays = null;
          persist(); render();
          break;

        case 'tray-reset':
          if (!cat) break;
          cat.trays = recommendTrays(cat.portion);
          cat.traysEdited = false;
          persist(); render();
          break;

        case 'tray+':
        case 'tray-': {
          if (!cat) break;
          const plan = CONFIG.plans[cat.portion];
          const key = btn.dataset.value;
          if (!cat.trays) cat.trays = recommendTrays(cat.portion);
          const cur = cat.trays[key] || 0;
          if (act === 'tray+') {
            if (trayTotal(cat.trays) >= plan.trays) break;
            cat.trays[key] = cur + 1;
          } else {
            if (cur <= 0) break;
            cat.trays[key] = cur - 1;
            if (cat.trays[key] === 0) delete cat.trays[key];
          }
          cat.traysEdited = true;
          persist(); render();
          break;
        }

        case 'checkout':
          if (DEBUG) { console.info('[bound] payload:', payload()); showPayloadPreview(); }
          else goToCheckout();
          break;
      }
    });

    root.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('input')) {
        e.preventDefault();
        if (canAdvance()) next();
      }
    });

    /* ================= PAYLOAD (debug) ================= */

    function payload() {
      return {
        schemaVersion: state.schemaVersion,
        configVersion: CONFIG.version,
        sessionId: state.sessionId,
        email: state.email,
        traySize: TRAY,
        cats: state.cats.map(cat => {
          const boxes = cat.portion ? deriveBoxes(cat) : null;
          const mv = monthlyVariantFor(cat.portion);
          return {
            id: cat.id,
            name: cat.name,
            age: cat.age,
            portion: cat.portion,
            traysEdited: cat.traysEdited,
            planVariantId: mv ? mv.variantId : null,
            planSellingPlanId: mv ? mv.sellingPlanId : null,
            planPrice: mv ? mv.price : null,
            cycleDays: boxes ? boxes.renewal.cycleDays : null,
            cartItem: boxes ? cartItemFor(cat) : null
          };
        })
      };
    }

    function wireEndScreen() {
      root.querySelector('[data-act="end-back"]')?.addEventListener('click', () => goTo('plan'));
      root.querySelector('[data-act="reset"]')?.addEventListener('click', () => {
        try { localStorage.removeItem(STORE_KEY); } catch {}
        location.href = location.pathname + (DEBUG ? '?debug=1' : '');
      });
    }

    function showPayloadPreview() {
      root.innerHTML = `
        ${brandHeader()}
        <h1 class="bd-title">Captured values</h1>
        <p class="bd-lede">Debug mode — no cart was created. This is what checkout would send.</p>
        <div class="bd-card">
          <h3>Still to come from the client</h3>
          <ul class="bd-todo">${PENDING_ASSETS.map(a => `<li>${esc(a)}</li>`).join('')}</ul>
          <h3>Open questions</h3>
          <ul class="bd-todo bd-todo--q">${OPEN_QUESTIONS.map(q => `<li>${esc(q)}</li>`).join('')}</ul>
        </div>
        <pre class="bd-payload">${esc(JSON.stringify(payload(), null, 2))}</pre>
        <div class="bd-nav">
          <button type="button" class="bd-back" data-act="end-back">‹ Back to plan</button>
          <button type="button" class="bd-ghost" data-act="reset">Start again</button>
        </div>`;
      wireEndScreen();
    }

    window.__bound = {
      state, payload, deriveBoxes, resolveVariant, recommendTrays,
      cartItemFor, goToCheckout, CONFIG, CATALOG, ASSETS
    };

    /* ================= BOOT ================= */

    const urlStep = PARAMS.get('step');
    if (urlStep && stepAt(urlStep)) {
      let target = urlStep;
      for (const s of active()) {
        if (s.id === urlStep) break;
        if (!s.validate(state)) { target = s.id; break; }
      }
      goTo(target, { push: false });
    } else {
      render();
    }

  } catch (e) {
    fail('WIZARD FAILED', e);
  }
})();